import { Button, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { DataTable } from "@/shared/components/DataTable";
import type { PaginatedQueryResult } from "@/shared/hooks/usePaginatedQuery";
import type { FailedJob } from "../types";

interface FailedJobsTableProps {
	query: PaginatedQueryResult<FailedJob>;
	canRetry: boolean;
	retryingId: string | null;
	onRetry: (job: FailedJob) => void;
}

/**
 * Navbatning yiqilgan joblari.
 *
 * ⚠️⚠️ **«O'chirish» tugmasi YO'Q va qo'shilmaydi** (kontrakt §6.3, spec
 * §8.2): job o'chirish va navbat tozalash domen invariantlarini jimgina
 * buzadi va qaytarib bo'lmaydi. Backendda ham bunday marshrut yo'q.
 *
 * ⚠️ Backend `retry` ni FAQAT `failed` holatidagi job uchun bajaradi va
 * boshqasida 404 beradi (eskirgan sahifa, ikki marta bosish). Xabar
 * `getErrorMessage` orqali xom ko'rsatiladi.
 */
export function FailedJobsTable({ query, canRetry, retryingId, onRetry }: FailedJobsTableProps) {
	const { t } = useTranslation("jobs");

	const columns: ColumnsType<FailedJob> = [
		{ title: t("failedJobs.columns.id"), dataIndex: "id", key: "id", width: 90 },
		{ title: t("failedJobs.columns.job"), dataIndex: "name", key: "name" },
		{
			title: t("failedJobs.columns.attempts"),
			dataIndex: "attemptsMade",
			key: "attemptsMade",
			width: 110,
		},
		{
			title: t("failedJobs.columns.failedReason"),
			key: "failedReason",
			// ⚠️ Xato sababi (stack trace) uzun bo'lishi mumkin — qisqartirib
			// ko'rsatiladi, lekin to'liq matn ochiladi.
			render: (_, row) => (
				<Typography.Paragraph className="!mb-0" ellipsis={{ rows: 2, expandable: true }}>
					{row.failedReason ?? "—"}
				</Typography.Paragraph>
			),
		},
		{
			title: t("failedJobs.columns.time"),
			key: "timestamp",
			render: (_, row) => dayjs(row.timestamp).format("DD.MM.YYYY HH:mm:ss"),
		},
	];

	if (canRetry) {
		columns.push({
			title: "",
			key: "actions",
			render: (_, row) => (
				<Button
					type="link"
					loading={retryingId === row.id}
					onClick={() => onRetry(row)}
					aria-label={t("failedJobs.retryAriaLabel", { name: row.name })}
				>
					{t("failedJobs.retry")}
				</Button>
			),
		});
	}

	return <DataTable<FailedJob> columns={columns} query={query} />;
}
